<template>
  <div class="min-h-screen bg-linear-to-br from-brand-blue-50 via-white to-brand-red-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex text-gray-900 dark:text-white">
    <!-- Left side - Branding -->
    <div class="hidden lg:flex lg:w-1/2 bg-linear-to-br from-brand-blue-600 via-brand-blue-700 to-brand-blue-800 p-12 relative overflow-hidden">
      <!-- Background Image -->
      <div class="absolute inset-0 bg-cover bg-center opacity-80 bg-splash" />

      <!-- Gradient Overlay -->
      <div class="absolute inset-0 bg-linear-to-br from-brand-blue-600/90 via-brand-blue-700/80 to-brand-blue-800/90" />

      <!-- Animated background blobs -->
      <div class="absolute inset-0">
        <div class="absolute top-20 left-20 w-96 h-96 bg-brand-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob" />
        <div class="absolute top-40 right-20 w-96 h-96 bg-brand-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000" />
        <div class="absolute -bottom-8 left-1/2 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-4000" />
      </div>

      <!-- Content -->
      <div class="relative z-10 flex flex-col justify-between w-full">
        <!-- Logo -->
        <div>
          <NuxtLink
            to="/"
            class="inline-block"
          >
            <img
              src="~/assets/images/logo_light.png"
              alt="Ambulance Présent 2.0"
              class="h-16 w-auto drop-shadow-lg"
              loading="eager"
              fetchpriority="high"
            >
          </NuxtLink>
        </div>

        <!-- Main content -->
        <div class="space-y-8">
          <div>
            <h1 class="text-5xl font-black text-white leading-tight drop-shadow-lg">
              Bienvenue chez<br>
              <span class="text-red-300">Ambulance Présent 2.0</span>
            </h1>
            <p class="mt-6 text-xl text-brand-blue-100 leading-relaxed">
              Votre espace volontaire pour gérer vos achats de tenues, et bien plus ...
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-2 text-brand-blue-100">
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-sm font-medium">Connexion sécurisée</span>
          </div>
          <div class="h-4 w-px bg-brand-blue-400" />
          <div class="flex items-center gap-2 text-brand-blue-100">
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-sm font-medium">Données protégées</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right side - Form -->
    <div class="flex-1 flex items-center justify-center px-4 py-8 sm:p-8 lg:p-12">
      <div class="w-full max-w-md">
        <!-- Mobile logo -->
        <div class="lg:hidden mb-8 text-center">
          <NuxtLink
            to="/"
            class="inline-block"
          >
            <img
              src="~/assets/images/logo.png"
              alt="Ambulance Présent 2.0"
              class="h-16 w-auto mx-auto"
              loading="eager"
            >
          </NuxtLink>
        </div>

        <!-- Form content -->
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
useHead({
  link: [
    { key: 'manifest', rel: 'manifest', href: '/volontaires.webmanifest' },
    { rel: 'apple-touch-icon', href: '/icons/volontaires-apple-touch.png' },
    ...pwaSplashLinks,
  ],
  meta: [
    { name: 'theme-color', content: '#295cb0' },
    { name: 'mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-title', content: 'AP 2.0' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
  ],
})
</script>

<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

.bg-splash {
  background-image: url('/images/splash.jpg');
  background-size: cover;
  background-position: center;
}
</style>
